import { PaperClipIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React, { useRef, useState } from 'react'

import { TextInput } from '@/components/forms/TextInput'
import { Toggle } from '@/components/forms/Toggle'
import { DocumentCheckIcon, TrashIcon } from '@/components/icons'
import { Modal } from '@/components/ui/Modal'
import { createRepairOrder, createRepairOrderFile } from '@/services/repairOrders.service'
import { toast } from '@/utils/helpers'

import { ACCEPTED_EXTENSIONS } from './constants'

// Rows are add/removeable, so each row needs a stable key independent of position.
const makeRow = () => ({ id: crypto.randomUUID(), roNumber: '', files: [], error: null })
const makeRows = (count) => Array.from({ length: count }, () => makeRow())

// Store each selected file with a client-side id so duplicate files can coexist
// and list/remove operations don't depend on array indexes.
const wrapFile = (raw) => ({ id: crypto.randomUUID(), raw })

// Format accepted by <input type="datetime-local"> — local wall-clock time, no zone.
const LOCAL_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm'
const DEFAULT_ROW_COUNT = 15

const AddRepairOrderModal = ({ open, setOpen, dealerId, onSuccess }) => {
  const [rows, setRows] = useState(() => makeRows(DEFAULT_ROW_COUNT))
  const [loading, setLoading] = useState(false)
  const [bulkMode, setBulkMode] = useState(false)
  const [bulkText, setBulkText] = useState('')
  // Once a pasted list has been applied to the rows, hide the "Paste a list" link until the
  // modal is reopened. Re-entering bulk mode would show an empty textarea and invite the user
  // to clobber the rows they just built.
  const [bulkUsed, setBulkUsed] = useState(false)

  // Received-at override: defaults to "now" at submit time unless the user opts in to
  // log a different receipt time (e.g. the RO was emailed at 8am but entered at 10am —
  // the 24h overdue clock needs to start at 8am).
  const [overrideReceivedAt, setOverrideReceivedAt] = useState(false)
  const [receivedAt, setReceivedAt] = useState('')

  const fileInputRefs = useRef({})

  const handleToggleOverride = (checked) => {
    setOverrideReceivedAt(checked)
    if (checked && !receivedAt) {
      setReceivedAt(dayjs().format(LOCAL_DATETIME_FORMAT))
    }
  }

  const updateRow = (index, value) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, roNumber: value, error: null } : r)),
    )
  }

  const addFiles = (index, newFiles) => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === index ? { ...r, files: [...r.files, ...newFiles.map(wrapFile)], error: null } : r,
      ),
    )
  }

  const removeFile = (rowIndex, fileId) => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === rowIndex ? { ...r, files: r.files.filter((f) => f.id !== fileId) } : r,
      ),
    )
  }

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index))
  }

  const addRow = () => {
    setRows((prev) => [...prev, makeRow()])
  }

  const applyBulkList = () => {
    const ids = bulkText
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
    if (ids.length === 0) return
    const newRows = ids.map((roId) => ({ ...makeRow(), roNumber: roId }))
    // Pad to at least DEFAULT_ROW_COUNT so there's room to add more
    while (newRows.length < DEFAULT_ROW_COUNT) {
      newRows.push(makeRow())
    }
    setRows(newRows)
    setBulkText('')
    setBulkMode(false)
    setBulkUsed(true)
  }

  const reset = () => {
    setRows(makeRows(DEFAULT_ROW_COUNT))
    setBulkMode(false)
    setBulkText('')
    setBulkUsed(false)
    setOverrideReceivedAt(false)
    setReceivedAt('')
    fileInputRefs.current = {}
  }

  const handleSubmit = async () => {
    const validRows = rows.filter((r) => r.roNumber.trim() !== '')
    const orphanedFileRows = rows.filter((r) => r.roNumber.trim() === '' && r.files.length > 0)

    if (orphanedFileRows.length > 0) {
      toast('Files cannot be uploaded without a Repair Order ID.', 'error')
      return
    }

    if (validRows.length === 0) {
      toast('Please enter at least one Repair Order ID.', 'error')
      return
    }

    if (overrideReceivedAt && !receivedAt) {
      toast('Please choose a received time or turn off the override.', 'error')
      return
    }

    setLoading(true)

    // One timestamp for the whole batch — dayjs parses the local wall-clock string
    // using the browser's zone, then toISOString() emits UTC for the API. When the
    // override is off, the backend defaults `received_at` to the row's insert time.
    const receivedAtIso = overrideReceivedAt && receivedAt ? dayjs(receivedAt).toISOString() : null

    // Create all ROs, suppressing per-item error toasts by passing a no-op error handler
    const results = await Promise.all(
      validRows.map(async (r) => {
        let errorMessage = null
        const payload = { roNumber: r.roNumber.trim(), status: 'Not Started' }
        if (receivedAtIso) payload.receivedAt = receivedAtIso

        const response = await createRepairOrder(
          dealerId,
          payload,
          () => {},
          (err) => {
            errorMessage = typeof err === 'string' ? err : err?.message || 'Failed to create'
          },
          () => {},
        )

        // Upload files if RO was created successfully
        if (response && r.files.length > 0) {
          try {
            await Promise.all(
              r.files.map(({ raw }) => {
                const formData = new FormData()
                formData.append('file', raw)
                formData.append('display_name', raw.name)
                return createRepairOrderFile(dealerId, response.id, formData)
              }),
            )
          } catch {
            // RO was created but file upload failed — don't treat as a full failure
            errorMessage = 'RO created but file upload failed'
          }
        }

        return { roNumber: r.roNumber, files: r.files, response, errorMessage }
      }),
    )

    const successes = results.filter((r) => r.response !== null)
    const failures = results.filter((r) => r.response === null)

    setLoading(false)

    if (failures.length === 0) {
      // All succeeded — close modal
      toast(`${successes.length} repair order${successes.length > 1 ? 's' : ''} added.`, 'success')
      reset()
      setOpen(false)
      onSuccess()
      return
    }

    if (successes.length > 0) {
      onSuccess()
    }

    // Keep modal open with only failed rows, showing inline errors
    const failedRows = failures.map((f) => ({
      ...makeRow(),
      roNumber: f.roNumber,
      files: f.files,
      error: f.errorMessage,
    }))
    setRows(failedRows)

    const failedCount = failures.length
    const successCount = successes.length
    if (successCount > 0) {
      toast(
        `${successCount} added, ${failedCount} failed. Fix the errors below and try again.`,
        'error',
      )
    } else {
      toast(
        `${
          failedCount > 1 ? `All ${failedCount} repair orders` : 'Repair order'
        } failed to create. Fix the errors below and try again.`,
        'error',
      )
    }
  }

  return (
    <Modal
      open={open}
      setOpen={(val) => {
        if (!val) reset()
        setOpen(val)
      }}
      onClose={() => {}}
      title="Add Repair Orders"
      description="Submit Repair Orders to WPC to process."
      width="large"
      loading={loading}
      content={
        <div className="space-y-4">
          {!bulkUsed && (
            <div className="mb-2 flex justify-end">
              <button
                type="button"
                className="text-sm text-blue-800 underline hover:text-blue-900"
                onClick={() => {
                  setBulkMode(!bulkMode)
                  setBulkText('')
                }}
              >
                {bulkMode ? 'Back to individual inputs' : 'Paste a list'}
              </button>
            </div>
          )}

          {bulkMode ? (
            <div className="bg-background space-y-3 rounded-xl p-6">
              <label
                htmlFor="bulk-ro-input"
                className="text-charcoal-700 mb-1 block text-[10px] font-bold uppercase tracking-widest"
              >
                Paste RO numbers (one per line)
              </label>
              <textarea
                id="bulk-ro-input"
                className="border-charcoal-200 w-full rounded-lg border bg-white p-3 text-sm"
                rows={10}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={'RO-001\nRO-002\nRO-003'}
              />
              <button
                type="button"
                className="rounded border border-blue-800 px-4 py-2 text-xs font-bold uppercase tracking-widest text-blue-800 hover:bg-blue-800/5"
                onClick={applyBulkList}
                disabled={bulkText.trim() === ''}
              >
                Apply List
              </button>
            </div>
          ) : (
            <div className="bg-background max-h-[400px] space-y-3 overflow-y-auto rounded-xl p-6">
              {rows.map((row, index) => (
                <div key={row.id}>
                  <div className="flex items-start gap-3">
                    <div className="w-48 shrink-0">
                      <label
                        htmlFor={`ro-id-${index}`}
                        className="text-charcoal-700 mb-1 block text-[10px] font-bold uppercase tracking-widest"
                      >
                        Repair Order ID
                      </label>
                      <TextInput
                        id={`ro-id-${index}`}
                        value={row.roNumber}
                        onChange={(e) => updateRow(index, e.target.value)}
                        placeholder="Enter RO number"
                        className={row.error ? 'border-red' : ''}
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        htmlFor={`ro-files-${index}`}
                        className="text-charcoal-700 mb-1 block text-[10px] font-bold uppercase tracking-widest"
                      >
                        Attachments
                      </label>
                      <input
                        id={`ro-files-${index}`}
                        ref={(el) => {
                          fileInputRefs.current[index] = el
                        }}
                        type="file"
                        multiple
                        accept={ACCEPTED_EXTENSIONS.join(',')}
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files.length > 0) {
                            addFiles(index, Array.from(e.target.files))
                          }
                          e.target.value = ''
                        }}
                      />
                      {row.files.length > 0 && (
                        <div className="mb-2 space-y-1">
                          {row.files.map((entry) => (
                            <div
                              key={entry.id}
                              className="flex items-center justify-between text-blue-800"
                            >
                              <div className="flex items-center overflow-x-hidden">
                                <DocumentCheckIcon className="mr-2 h-5 w-5 shrink-0" />
                                <span className="truncate text-sm underline">
                                  {entry.raw.name}
                                </span>
                              </div>
                              <button
                                type="button"
                                aria-label="Delete"
                                className="inline-flex shrink-0 items-center rounded-lg bg-transparent text-center text-sm font-medium text-gray-700 hover:bg-gray-100"
                                onClick={() => removeFile(index, entry.id)}
                              >
                                <TrashIcon className="text-charcoal-800 h-5 w-5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        className="border-charcoal-200 text-charcoal-700 flex h-[46px] w-full items-center gap-2 rounded-lg border border-dashed bg-white px-3 text-sm transition-colors hover:border-blue-800 hover:text-blue-800"
                        onClick={() => fileInputRefs.current[index]?.click()}
                      >
                        <PaperClipIcon className="h-4 w-4 shrink-0" />
                        <span>
                          {row.files.length > 0 ? 'Add more files...' : 'Choose files...'}
                        </span>
                      </button>
                    </div>
                    {rows.length > 1 && (
                      <button
                        type="button"
                        className="text-charcoal-700 hover:text-red mt-6 text-sm"
                        onClick={() => removeRow(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {row.error && <p className="text-red mt-1 text-xs">{row.error}</p>}
                </div>
              ))}

              <button
                type="button"
                className="mt-2 flex items-center gap-2 rounded border border-blue-800 px-4 py-2 text-xs font-bold uppercase tracking-widest text-blue-800 hover:bg-blue-800/5"
                onClick={addRow}
              >
                + Add More
              </button>
            </div>
          )}

          {/* Received At — applies to every RO in the batch. Defaults to "now"; the
              override is for cases like "got the RO email at 8am, entering at 10am"
              where the 24h overdue clock should start at 8am. */}
          <div className="bg-background rounded-xl p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-charcoal-700 block text-[10px] font-bold uppercase tracking-widest">
                  Received At
                </p>
                <p className="text-charcoal-500 mt-1 text-xs">
                  {overrideReceivedAt
                    ? 'Overdue clock starts from the time below.'
                    : 'Defaults to now. Override if the RO arrived earlier than the time it was entered.'}
                </p>
              </div>
              <Toggle
                label="Override"
                checked={overrideReceivedAt}
                onChange={handleToggleOverride}
              />
            </div>
            {overrideReceivedAt && (
              <div className="mt-3 w-60">
                <TextInput
                  aria-label="Received at"
                  type="datetime-local"
                  value={receivedAt}
                  max={dayjs().format(LOCAL_DATETIME_FORMAT)}
                  onChange={(e) => setReceivedAt(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
      }
      actions={[
        { type: 'cancel', label: 'Cancel' },
        {
          type: 'submit',
          label: 'Add Repair Orders',
          onClick: handleSubmit,
          disabled:
            bulkMode || rows.every((r) => r.roNumber.trim() === '' && r.files.length === 0),
        },
      ]}
    />
  )
}

AddRepairOrderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  dealerId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
}

export default AddRepairOrderModal
